import {
  Anchor,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useTutorialsAvailable } from "../hooks/useTutorialsAvailable";
import { getCurrentUser } from "../shared/getCurrentUser";
import {
  imgDinLabelLaptop,
  imgDinLabelLogoLetras,
  imgMailIcon,
  imgTutoriales,
} from "../shared/images";
import { FeatureLabelCards } from "./FeatureLabelCards";
import { NavBarContainer } from "./NavBarContainer";

export function MarketingHome() {
  const { t } = useTranslation();
  const showTutorials = useTutorialsAvailable();
  const alreadyBought = Boolean(getCurrentUser()?.Bought);
  const downloadLabel = alreadyBought
    ? t("nav.download")
    : t("nav.downloadDemo");

  return (
    <NavBarContainer fullBleed showFooter>
      {/* Hero — brand first, one composition */}
      <section className="marketing-hero">
        <Container size="lg" className="marketing-hero-inner" py="xl">
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg" className="marketing-hero-copy">
                <Title
                  order={1}
                  fz={{ base: 42, sm: 52, md: 60 }}
                  lh={1.05}
                  c="charcoal.8"
                >
                  <Text span c="charcoal.5" inherit>
                    Din
                  </Text>
                  <Text span c="brand.6" inherit>
                    Label
                  </Text>
                </Title>
                <Title
                  order={2}
                  fz={{ base: 22, sm: 26 }}
                  fw={500}
                  c="charcoal.7"
                  style={{ fontFamily: "var(--mantine-font-family)" }}
                >
                  {t("marketing.headline")}
                </Title>
                <Text size="lg" c="dimmed" maw={440}>
                  {t("marketing.slogan")}
                </Text>
                <Group gap="md" className="marketing-hero-ctas">
                  <Button
                    component={Link}
                    to="/install"
                    size="lg"
                    color="brand"
                  >
                    {downloadLabel}
                  </Button>
                  <Button
                    component={Link}
                    to="/buy"
                    size="lg"
                    variant="outline"
                    color="charcoal"
                  >
                    {t("nav.buy")}
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Box className="marketing-hero-visual" ta="center">
                <Image
                  src={imgDinLabelLaptop}
                  alt="DinLabel"
                  maw={520}
                  mx="auto"
                  style={{
                    filter: "drop-shadow(0 24px 48px rgba(44, 45, 48, 0.18))",
                  }}
                />
              </Box>
            </Grid.Col>
          </Grid>
        </Container>
      </section>

      {/* Features */}
      <Box className="marketing-section" bg="white">
        <Container size="lg">
          <Stack gap="xl">
            <Stack gap="xs" maw={520}>
              <Title order={2} c="charcoal.8">
                {t("marketing.featuresTitle")}
              </Title>
              <Text c="dimmed">{t("marketing.featuresSubtitle")}</Text>
              <Anchor component={Link} to="/barcodes" fw={600} underline="hover">
                {t("marketing.seeBarcodeTypes")}
              </Anchor>
            </Stack>
            <FeatureLabelCards />
          </Stack>
        </Container>
      </Box>

      {/* Printer compatibility */}
      <Box className="marketing-section marketing-printers" bg="charcoal.8">
        <Container size="lg">
          <Stack gap="lg" maw={720} mx="auto" ta="center">
            <Title order={2} c="white">
              {t("marketing.printersTitle")}
            </Title>
            <Text c="gray.4" size="lg">
              {t("marketing.printersSubtitle")}
            </Text>
            <Stack gap="md" maw={600} mx="auto" ta="left">
              <Text
                c="brand.4"
                fw={700}
                fz={{ base: 16, sm: 18 }}
              >
                {t("marketing.printersTypesTitle")}
              </Text>
              <Stack gap="sm">
                <Text c="gray.4">
                  <Text span fw={700} c="white">
                    {t("marketing.printersThermalLabel")}
                  </Text>{" "}
                  {t("marketing.printersThermalBody")}
                </Text>
                <Text c="gray.4">
                  <Text span fw={700} c="white">
                    {t("marketing.printersLaserLabel")}
                  </Text>{" "}
                  {t("marketing.printersLaserBody")}
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Why DinLabel */}
      <Box
        className="marketing-section"
        style={{
          background:
            "linear-gradient(180deg, #fff8f2 0%, #ffffff 100%)",
        }}
      >
        <Container size="lg">
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Image
                src={imgDinLabelLogoLetras}
                alt="DinLabel"
                maw={300}
                mx="auto"
                radius="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="md">
                <Title order={2} c="charcoal.8">
                  {t("marketing.whyTitle")}
                </Title>
                <Stack gap="sm" maw={520}>
                  {(
                    [
                      ["whyCompatLabel", "whyCompatBody"],
                      ["whyCostLabel", "whyCostBody"],
                      ["whySupportLabel", "whySupportBody"],
                    ] as const
                  ).map(([labelKey, bodyKey]) => (
                    <Text key={labelKey} size="lg" c="dimmed">
                      <Text span fw={700} c="charcoal.8">
                        {t(`marketing.${labelKey}`)}
                      </Text>{" "}
                      {t(`marketing.${bodyKey}`)}
                    </Text>
                  ))}
                </Stack>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Try free CTA strip */}
      <Box
        py={56}
        style={{
          background: "var(--mantine-color-charcoal-8)",
        }}
      >
        <Container size="lg">
          <Group
            justify="space-between"
            align="center"
            wrap="wrap"
            gap="lg"
          >
            <div>
              <Title order={2} c="white" mb={6}>
                {t("marketing.tryTitle")}
              </Title>
              <Text c="gray.4" maw={420}>
                {t("marketing.trySubtitle")}
              </Text>
            </div>
            <Button component={Link} to="/install" size="lg" color="brand">
              {downloadLabel}
            </Button>
          </Group>
        </Container>
      </Box>

      {/* Help */}
      <Box className="marketing-section" bg="white">
        <Container size="lg">
          <Stack gap="xl">
            <Title order={2} ta="center" c="charcoal.8">
              {t("marketing.needHelp")}
            </Title>
            <Grid>
              <Grid.Col span={{ base: 12, sm: showTutorials ? 6 : 12 }}>
                <Stack align="center" gap="sm">
                  <Image src={imgMailIcon} alt="" w={56} />
                  <Anchor
                    href="mailto:support@dinlabel.com"
                    fw={600}
                    c="brand.7"
                    size="lg"
                  >
                    {t("marketing.customerService")}
                  </Anchor>
                  <Text ta="center" size="sm" c="dimmed" maw={280}>
                    {t("marketing.customerServiceText")}
                  </Text>
                </Stack>
              </Grid.Col>
              {showTutorials && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <Stack align="center" gap="sm">
                    <Image src={imgTutoriales} alt="" w={56} />
                    <Anchor
                      component={Link}
                      to="/tutorials"
                      fw={600}
                      c="brand.7"
                      size="lg"
                    >
                      {t("nav.tutorials")}
                    </Anchor>
                    <Text ta="center" size="sm" c="dimmed" maw={280}>
                      {t("marketing.tutorialsText")}
                    </Text>
                  </Stack>
                </Grid.Col>
              )}
            </Grid>
          </Stack>
        </Container>
      </Box>
    </NavBarContainer>
  );
}
